import { useEffect, useRef, useState } from 'react';
import type { AdSyncSettingsDto, SyncRuleMode } from '@/api/types';
import {
  AD_SYNC_POR_OMISION, MODOS, bajaFrontlineSinLicencia, bajaSinInclusion, hayAplicar, leerSkus, skusATexto, sonIguales, validar,
} from './adSyncForm';
import { useGuardarConfiguracion } from './useGuardarConfiguracion';
import { ConfigCard, AvisoDeGuardado } from './ConfigCard';

const ETIQUETAS: Record<SyncRuleMode, { label: string; hint: string }> = {
  Off:        { label: 'jobs.sync.modeOff',        hint: 'jobs.sync.modeOffHint' },
  ReportOnly: { label: 'jobs.sync.modeReportOnly', hint: 'jobs.sync.modeReportOnlyHint' },
  Enforce:    { label: 'jobs.sync.modeEnforce',    hint: 'jobs.sync.modeEnforceHint' },
};

/** Los tres estados de una regla, como un grupo de opciones. */
function Modos({
  nombre, valor, onChange, t,
}: {
  nombre: string;
  valor: SyncRuleMode;
  onChange: (modo: SyncRuleMode) => void;
  t: (clave: string) => string;
}) {
  return (
    <>
      {MODOS.map(modo => (
        <label key={modo} className="jobs-choice">
          <input
            type="radio"
            name={nombre}
            value={modo}
            checked={valor === modo}
            onChange={() => onChange(modo)}
            aria-labelledby={`${nombre}-${modo}-etiqueta`}
            aria-describedby={`${nombre}-${modo}-pista`}
          />
          <span>
            <strong id={`${nombre}-${modo}-etiqueta`}>{t(ETIQUETAS[modo].label)}</strong>
            <small id={`${nombre}-${modo}-pista`}>{t(ETIQUETAS[modo].hint)}</small>
          </span>
        </label>
      ))}
    </>
  );
}

/**
 * Las reglas del sync del AD: **licencia activa**, **incorporación de practicantes** y **baja de
 * practicantes** (Sigma, oct-2026).
 *
 * Las tres arrancan APAGADAS: desplegar el código no cambia el comportamiento de nadie, y se encienden
 * de una en una, primero en «Solo reporte» (cuenta a quién afectaría, sin tocar a nadie) y después en
 * «Aplicar». Por eso esta tarjeta no guarda al elegir una opción, como la de fuente de datos, sino con un
 * botón: son cambios que deciden quién entra al padrón, y «Aplicar» pide confirmar antes.
 *
 * 🔴 Es la única tarjeta que manda `adSync` en el `PUT`. Las demás no lo mandan, y el backend lo entiende
 * como «no lo cambies»: así guardar la pestaña de dominios no pisa estas reglas.
 */
export function AdSyncCard() {
  const { t, data, isLoading, guardando, guardar, aviso, mensajeDeError } = useGuardarConfiguracion();

  // `null` significa «nadie ha editado»: la tarjeta refleja lo que hay guardado en el servidor, también tras un
  // refetch. En cuanto alguien edita, el borrador manda y un refetch (p. ej. al recuperar el foco de la
  // pestaña) ya no borra lo escrito a medias. Al guardar se vuelve a `null`, y la tarjeta muestra lo recién
  // guardado SIN pasar por un estado intermedio de «cargando» (que desmontaba y volvía a montar toda la tarjeta).
  const [form, setForm] = useState<AdSyncSettingsDto | null>(null);
  const [skusTexto, setSkusTexto] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const botonConfirmar = useRef<HTMLButtonElement>(null);

  // M3: al pedir confirmación el botón de «Guardar» se desmonta y el foco caería al <body>. Se lleva al botón
  // de confirmar para que quien navega con teclado no pierda su sitio.
  useEffect(() => {
    if (confirmando) botonConfirmar.current?.focus();
  }, [confirmando]);

  if (isLoading) return <p className="jobs-card-hint" role="status">{t('common.loading')}</p>;
  // Solo se reemplaza la tarjeta por el error si NO hay datos: un refetch fallido en segundo plano no debe
  // esconder lo que se está editando.
  if (!data) return <p className="jobs-alert jobs-alert--bad" role="alert">{t('jobs.sync.loadError')}</p>;

  // Con un backend anterior al cambio, `adSync` no trae los campos nuevos: se completan con los valores por omisión para
  // que la pantalla no vea una diferencia que no existe ni bloquee el guardado.
  const guardado: AdSyncSettingsDto = { ...AD_SYNC_POR_OMISION, ...data.adSync };
  const base = form ?? guardado;
  const texto = skusTexto ?? skusATexto(guardado.allowedLicenseSkuIds);
  const leidos = leerSkus(texto);
  const actual: AdSyncSettingsDto = { ...base, allowedLicenseSkuIds: leidos.skus };
  const errores = validar(actual, leidos.problemas);
  const hayCambios = !sonIguales(actual, guardado);

  const cambiar = (parcial: Partial<AdSyncSettingsDto>) => {
    setForm({ ...base, ...parcial });
    setConfirmando(false);
  };

  const guardarReglas = () => {
    setConfirmando(false);
    guardar({ adSync: actual }, t('jobs.sync.adRulesSaved'), () => { setForm(null); setSkusTexto(null); });
  };

  const alRevisar = () => {
    // «Aplicar» decide quién entra al padrón: pide una confirmación. Los otros estados se guardan directo.
    if (hayAplicar(actual)) setConfirmando(true);
    else guardarReglas();
  };

  return (
    <ConfigCard
      icono="fa-user-shield"
      titulo={t('jobs.sync.adRules')}
      descripcion={t('jobs.sync.adRulesDesc')}
      isWide
      pie={
        <>
          <AvisoDeGuardado aviso={aviso} error={mensajeDeError} />
          {data.updatedBy && (
            <p className="jobs-card-meta">{t('jobs.sync.updatedBy')} {data.updatedBy}</p>
          )}
        </>
      }
    >
      <div className="jobs-adsync-columns">
        {/* ── Licencia ─────────────────────────────────────────────────────────────────────────── */}
        <fieldset className="jobs-fieldset" disabled={guardando}>
          <legend>{t('jobs.sync.license')}</legend>
          <p className="jobs-card-hint">{t('jobs.sync.licenseDesc')}</p>
          <Modos
            nombre="licenseMode"
            valor={base.licenseMode}
            onChange={modo => cambiar({ licenseMode: modo })}
            t={t}
          />

          <label className="jobs-field-label" htmlFor="adSyncSkus">{t('jobs.sync.licenseSkus')}</label>
          <textarea
            id="adSyncSkus"
            className="jobs-textarea"
            value={texto}
            onChange={e => { setSkusTexto(e.target.value); setConfirmando(false); }}
            rows={5}
            spellCheck={false}
            autoCapitalize="none"
          />
          <p className="jobs-card-meta">
            {t('jobs.sync.licenseSkusHint')} · {t('jobs.sync.licenseSkusCount', { n: leidos.skus.length })}
          </p>

          {leidos.problemas.length > 0 && (
            <ul
              className={`jobs-problem-list jobs-alert ${
                leidos.problemas.every(p => p.tipo === 'duplicado') ? 'jobs-alert--info' : 'jobs-alert--bad'
              }`}
              role={leidos.problemas.every(p => p.tipo === 'duplicado') ? 'status' : 'alert'}
            >
              {leidos.problemas.map((problema, i) => (
                <li key={`${problema.tipo}-${problema.valor}-${i}`}>
                  <code>{problema.valor}</code>{' — '}
                  {problema.tipo === 'formato' ? t('jobs.sync.licenseSkuBad') : t('jobs.sync.domainDuplicate')}
                </li>
              ))}
            </ul>
          )}
          {errores.isLicenseListMissing && (
            <p className="jobs-alert jobs-alert--bad" role="alert">{t('jobs.sync.licenseSkusEmpty')}</p>
          )}

          <label className="jobs-field-label" htmlFor="adSyncMaxLicense">{t('jobs.sync.licenseMax')}</label>
          <input
            id="adSyncMaxLicense"
            type="number"
            className="jobs-number-input"
            min={1}
            max={100}
            value={Number.isNaN(base.maxLicenseDiscardPercent) ? '' : base.maxLicenseDiscardPercent}
            onChange={e => cambiar({ maxLicenseDiscardPercent: e.target.valueAsNumber })}
          />
          <p className="jobs-card-meta">{t('jobs.sync.licenseMaxHint')}</p>
          {errores.isLicenseCapInvalid && (
            <p className="jobs-alert jobs-alert--bad" role="alert">{t('jobs.sync.percentInvalid')}</p>
          )}
        </fieldset>

        {/* ── Incorporación de practicantes ────────────────────────────────────────────────────── */}
        <fieldset className="jobs-fieldset" disabled={guardando}>
          <legend>{t('jobs.sync.practicantes')}</legend>
          <p className="jobs-card-hint">{t('jobs.sync.practicantesDesc')}</p>
          <Modos
            nombre="practicanteInclusionMode"
            valor={base.practicanteInclusionMode}
            onChange={modo => cambiar({ practicanteInclusionMode: modo })}
            t={t}
          />
        </fieldset>

        {/* ── Baja de practicantes ─────────────────────────────────────────────────────────────── */}
        <fieldset className="jobs-fieldset" disabled={guardando}>
          <legend>{t('jobs.sync.deactivation')}</legend>
          <p className="jobs-card-hint">{t('jobs.sync.deactivationDesc')}</p>
          <Modos
            nombre="practicanteDeactivationMode"
            valor={base.practicanteDeactivationMode}
            onChange={modo => cambiar({ practicanteDeactivationMode: modo })}
            t={t}
          />
          {bajaSinInclusion(actual) && (
            <p className="jobs-alert jobs-alert--info" role="status">{t('jobs.sync.deactivationNeedsInclusion')}</p>
          )}

          <label className="jobs-field-label" htmlFor="adSyncMaxBaja">{t('jobs.sync.deactivationMax')}</label>
          <input
            id="adSyncMaxBaja"
            type="number"
            className="jobs-number-input"
            min={1}
            max={100}
            value={Number.isNaN(base.maxPracticanteDeactivationPercent) ? '' : base.maxPracticanteDeactivationPercent}
            onChange={e => cambiar({ maxPracticanteDeactivationPercent: e.target.valueAsNumber })}
          />
          <p className="jobs-card-meta">{t('jobs.sync.deactivationMaxHint')}</p>
          {errores.isDeactivationCapInvalid && (
            <p className="jobs-alert jobs-alert--bad" role="alert">{t('jobs.sync.percentInvalid')}</p>
          )}
        </fieldset>

        {/* ── Baja a Frontline por licencia ────────────────────────────────────────────────────── */}
        <fieldset className="jobs-fieldset" disabled={guardando}>
          <legend>{t('jobs.sync.demotion')}</legend>
          <p className="jobs-card-hint">{t('jobs.sync.demotionDesc')}</p>
          <Modos
            nombre="licenseDemotionMode"
            valor={base.licenseDemotionMode}
            onChange={modo => cambiar({ licenseDemotionMode: modo })}
            t={t}
          />
          {bajaFrontlineSinLicencia(actual) && (
            <p className="jobs-alert jobs-alert--info" role="status">{t('jobs.sync.demotionNeedsLicense')}</p>
          )}

          <label className="jobs-field-label" htmlFor="adSyncMaxDemotion">{t('jobs.sync.demotionMax')}</label>
          <input
            id="adSyncMaxDemotion"
            type="number"
            className="jobs-number-input"
            min={1}
            max={100}
            value={Number.isNaN(base.maxLicenseDemotionPercent) ? '' : base.maxLicenseDemotionPercent}
            onChange={e => cambiar({ maxLicenseDemotionPercent: e.target.valueAsNumber })}
          />
          <p className="jobs-card-meta">{t('jobs.sync.demotionMaxHint')}</p>
          {errores.isDemotionCapInvalid && (
            <p className="jobs-alert jobs-alert--bad" role="alert">{t('jobs.sync.percentInvalid')}</p>
          )}
        </fieldset>
      </div>

      <div className="jobs-fieldset">
        {confirmando && (
          <p className="jobs-alert jobs-alert--info" role="alert">{t('jobs.sync.adRulesConfirmEnforce')}</p>
        )}
        <div className="jobs-card-actions">
          {!confirmando ? (
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={!hayCambios || errores.hasErrors || guardando}
              onClick={alRevisar}
            >
              {t('jobs.sync.adRulesSave')}
            </button>
          ) : (
            <>
              <button
                ref={botonConfirmar}
                type="button"
                className="btn btn--primary btn--sm"
                onClick={guardarReglas}
                disabled={guardando || errores.hasErrors}
              >
                {t('jobs.sync.adRulesConfirm')}
              </button>
              <button type="button" className="btn btn--secondary btn--sm" onClick={() => setConfirmando(false)} disabled={guardando}>
                {t('jobs.sync.domainsCancel')}
              </button>
            </>
          )}
        </div>
      </div>
    </ConfigCard>
  );
}
